/**
 * Notifications API
 * Handles all notification-related API calls
 */

import { supabase } from '../lib/supabase';
import { log } from '../utils/logger';

export const notificationAPI = {
  /**
   * Fetch all notifications for the current user
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Notifications data
   */
  async getNotifications(options = {}) {
    try {
      const { includeArchived = false, limit = 50, offset = 0 } = options;
      
      let query = supabase
        .from('notifications')
        .select(`
          *,
          reservations!reservation_id(id, confirmation_number),
          teams!team_id(id, name),
          venues!venue_id(id, name)
        `)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (!includeArchived) {
        query = query.eq('is_archived', false);
      }

      const { data, error, count } = await query;

      if (error) {
        log.error('Error fetching notifications:', error);
        throw error;
      }

      return { data, count };
    } catch (error) {
      log.error('Failed to get notifications:', error);
      throw error;
    }
  },

  /**
   * Get unread notification count for the current user
   * @returns {Promise<number>} Count of unread notifications
   */
  async getUnreadCount() {
    try {
      const { data, error } = await supabase
        .rpc('get_unread_notification_count');

      if (error) {
        log.error('Error fetching unread count:', error);
        throw error;
      }

      return data || 0;
    } catch (error) {
      log.error('Failed to get unread notification count:', error);
      throw error;
    }
  },

  /**
   * Mark a notification as read
   * @param {string} notificationId - ID of the notification
   * @returns {Promise<boolean>} Success status
   */
  async markAsRead(notificationId) {
    try {
      const { data, error } = await supabase
        .rpc('mark_notification_read', { notification_id: notificationId });

      if (error) {
        log.error('Error marking notification as read:', error);
        throw error;
      }

      return data;
    } catch (error) {
      log.error('Failed to mark notification as read:', error);
      throw error;
    }
  },

  /**
   * Mark multiple notifications as read
   * @param {string[]} notificationIds - Array of notification IDs
   * @returns {Promise<Object>} Update result
   */
  async markMultipleAsRead(notificationIds) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .update({ 
          is_read: true, 
          read_at: new Date().toISOString() 
        })
        .in('id', notificationIds)
        .eq('is_read', false);

      if (error) {
        log.error('Error marking notifications as read:', error);
        throw error;
      }

      return data;
    } catch (error) {
      log.error('Failed to mark multiple notifications as read:', error);
      throw error;
    }
  },

  /**
   * Archive a notification
   * @param {string} notificationId - ID of the notification
   * @returns {Promise<boolean>} Success status
   */
  async archiveNotification(notificationId) {
    try {
      const { data, error } = await supabase
        .rpc('archive_notification', { notification_id: notificationId });

      if (error) {
        log.error('Error archiving notification:', error);
        throw error;
      }

      return data;
    } catch (error) {
      log.error('Failed to archive notification:', error);
      throw error;
    }
  },

  /**
   * Create a notification (for TeamRezy employees or system)
   * @param {Object} notificationData - Notification data
   * @returns {Promise<string>} Created notification ID
   */
  async createNotification(notificationData) {
    try {
      const {
        user_id,
        type,
        title,
        message,
        data = null,
        reservation_id = null,
        team_id = null,
        venue_id = null,
        from_user_id = null
      } = notificationData;

      const { data: result, error } = await supabase
        .rpc('create_notification', {
          p_user_id: user_id,
          p_type: type,
          p_title: title,
          p_message: message,
          p_data: data,
          p_reservation_id: reservation_id,
          p_team_id: team_id,
          p_venue_id: venue_id,
          p_from_user_id: from_user_id
        });

      if (error) {
        log.error('Error creating notification:', error);
        throw error;
      }

      log.info('✅ Notification created:', result);
      return result;
    } catch (error) {
      log.error('Failed to create notification:', error);
      throw error;
    }
  },

  /**
   * Mark all notifications as read for the current user
   * @returns {Promise<Object>} Update result
   */
  async markAllAsRead() {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .update({ 
          is_read: true, 
          read_at: new Date().toISOString() 
        })
        .eq('is_read', false)
        .eq('is_archived', false);

      if (error) {
        log.error('Error marking all notifications as read:', error);
        throw error;
      }

      return data;
    } catch (error) {
      log.error('Failed to mark all notifications as read:', error);
      throw error;
    }
  },

  /**
   * Subscribe to notification changes for real-time updates
   * @param {Function} callback - Callback function for updates
   * @returns {Object} Subscription object
   */
  subscribeToNotifications(callback) {
    const subscription = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${supabase.auth.user()?.id}`
        },
        (payload) => {
          log.info('Notification change:', payload);
          callback(payload);
        }
      )
      .subscribe();

    return subscription;
  }
};